import { EChartsOption } from "echarts";
import { Box, Container } from "@mui/material";
import { RopChart } from "../../../../main/components/RopChart";
import {
  numberPtBr,
  numberPtBrPercent01,
  toFixedNoRound,
} from "../../../../main/utils/utils";
import { colorList, generateColorArray } from "./GeneratedEnergyCharts";
import { EmptyTable } from "../../../clients/components/EmptyState";

export type GenerationComparisonChartProps = {
  hasIdeal?: boolean;
  energiaGerada: number | undefined;
  energiaPrevista: number | undefined;
  energiaIdeal: number | undefined;
  prReal: number | undefined;
  prPrevisto: number | undefined;
};

export default function GenerationComparisonChart(
  props: GenerationComparisonChartProps
) {
  const {
    hasIdeal,
    energiaGerada,
    energiaIdeal,
    energiaPrevista,
    prPrevisto,
    prReal,
  } = props;

  const isZeroData = hasIdeal ? energiaIdeal === 0 : prPrevisto === 0;
  const max = isZeroData ? 1 : hasIdeal ? energiaIdeal : prPrevisto;

  const GeradoOffsetTitle = hasIdeal ? ["90%", "90%"] : ["50%", "90%"];
  const GeradoOffsetDetails = hasIdeal ? ["90%", "110%"] : ["50%", "110%"];
  const EsperadoOffsetTitle = hasIdeal ? ["-90%", "90%"] : ["-50%", "90%"];
  const EsperadoOffsetDetails = hasIdeal ? ["-90%", "110%"] : ["-50%", "110%"];

  const gaugeData = [
    {
      value: isZeroData ? 0 : hasIdeal ? energiaGerada : prReal,
      name: hasIdeal ? "Gerada" : "Realizada",
      title: {
        offsetCenter: ["0%", "-50%"],
      },
      detail: {
        offsetCenter: ["0%", "-30%"],
      },
    },
    ...(energiaPrevista
      ? [
        {
          value: isZeroData ? 0 : hasIdeal ? energiaPrevista : prPrevisto,
          name: hasIdeal ? "Prevista" : "Previsto",
          title: {
            offsetCenter: ["0%", "-10%"],
          },
          detail: {
            offsetCenter: ["0%", "10%"],
            formatter(v: any) {
              const vTxt = toFixedNoRound(v, 0);
              const suffix = hasIdeal ? " MWh" : "";
              const percentage = v / (max ?? 0);
              const percentageTxt = numberPtBrPercent01(percentage, 0);
              return v > 0
                ? `${vTxt}${suffix} ${hasIdeal ? `(${percentageTxt})` : ""}`
                : "-";
            },
          },
        },
      ]
      : []),
    ...(hasIdeal
      ? [
        {
          value: isZeroData ? 0 : energiaIdeal ?? 0,
          name: "Ideal",
          title: {
            offsetCenter: ["0%", "30%"],
          },
          detail: {
            offsetCenter: ["0%", "50%"],
          },
        },
      ]
      : []),
  ];

  const options: EChartsOption = {
    title: {
      text: hasIdeal ? "Análise de Geração Diária" : "Performance Ratio",
      textStyle: {
        fontSize: 12,
      },
      subtext: "Efciência da usina ao converter irradiância solar em energia elétrica útil.",
      subtextStyle: {
        fontSize: 12,
        color: "#aaa",
      },
    },
    backgroundColor: "transparent",
    series: [
      {
        type: "gauge",
        startAngle: 90,
        endAngle: -180,
        radius: "70%",
        center: ["50%", "48%"],
        max,

        animation: false,
        color: generateColorArray(gaugeData.length),

        pointer: {
          show: false,
        },

        progress: {
          show: true,
          overlap: false,
          clip: false,
        },
        axisLine: {
          lineStyle: {
            width: 20,
          },
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },

        axisLabel: {
          show: false,
          distance: 50,
        },

        data: gaugeData,

        title: {
          fontSize: 14,
        },

        detail: {
          width: 100,
          height: 12,
          fontSize: 12,
          color: "inherit",
          borderColor: "inherit",
          borderRadius: 20,
          borderWidth: 1,
          formatter(v) {
            const vTxt = toFixedNoRound(v, 0);
            const percentage = v / (max ?? 0);
            const percentageTxt = numberPtBrPercent01(percentage);
            const suffix = hasIdeal ? " MWh" : "";
            return v > 0 ? `${vTxt}${suffix} (${percentageTxt})` : "-";
          },
        },
      },
      {
        type: "gauge",
        startAngle: 90,
        endAngle: -180,
        radius: "80%",
        max,

        animation: false,
        color: [...colorList],

        pointer: {
          show: false,
        },

        progress: {
          show: false,
        },
        axisLine: {
          show: false,
        },
        splitLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },

        axisLabel: {
          show: false,
          distance: 50,
        },

        title: {
          fontSize: 14,
        },
        detail: {
          width: 100,
          height: 12,
          fontSize: 12,
          color: "inherit",
          borderWidth: 0,
        },
        data: [
          {
            name: hasIdeal ? "Energia Gerada" : "PR Real",
            value: hasIdeal ? energiaGerada : prReal,
            title: {
              fontSize: 14,
              offsetCenter: GeradoOffsetTitle,
              color: "#FFF",
            },
            detail: {
              fontSize: 14,
              offsetCenter: GeradoOffsetDetails,
              color: "#FFF",
              formatter(v) {
                const vTxt = numberPtBr(v);
                const suffix = hasIdeal ? " MWh" : "%";
                return `${vTxt}${suffix}`;
              },
            },
          },
          {
            name: hasIdeal ? "Energia Prevista" : "PR Previsto",
            value: hasIdeal ? energiaPrevista : prPrevisto,
            title: {
              fontSize: 14,
              offsetCenter: EsperadoOffsetTitle,
              color: "#FFF",
            },
            detail: {
              fontSize: 14,
              offsetCenter: EsperadoOffsetDetails,
              color: "#FFF",
              formatter(v) {
                const vTxt = numberPtBr(v);
                const suffix = hasIdeal ? " MWh" : "";
                return `${vTxt}${suffix}`;
              },
            },
          },
          ...(hasIdeal
            ? [
              {
                name: "Energia Ideal",
                value: energiaIdeal,
                title: {
                  fontSize: 14,
                  offsetCenter: ["0%", "90%"],
                  color: "#FFF",
                },
                detail: {
                  fontSize: 14,
                  offsetCenter: ["0%", "110%"],
                  color: "#FFF",
                  formatter(v: any) {
                    const vTxt = numberPtBr(v);
                    const suffix = hasIdeal ? " MWh" : "";

                    return `${vTxt}${suffix}`;
                  },
                },
              },
            ]
            : []),
        ],
      },
    ],
  };

  const hasData =
    energiaIdeal || energiaPrevista || energiaGerada || prReal || prPrevisto;

  return (
    <>
      {hasData ? (
        <Container
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "90%",
            width: "100%",
          }}
        >
          <RopChart option={options} />
        </Container>
      ) : (
        <Box
          sx={{
            width: "100%",
            height: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <EmptyTable title="Nenhum dado encontrado!" />
        </Box>
      )}
    </>
  );
}
